import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import PGPStreamingPage from './pages/PGPStreamingPage';
import LoginPage from './pages/LoginPage';
import SchedulerPage from './pages/SchedulerPage';
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import StreamingPageV2 from './pages/StreamingPageV2';
import UnsignedPageV2 from './pages/UnsignedPageV2';
import AdminPage from './pages/AdminPage';
import SnoopSummaryPage from './pages/SnoopSummaryPage';
import DealsPage from './pages/DealsPage';
import LuminatePage from './pages/LuminatePage';
import MappingPage from './pages/MappingPage';
import PgpPage from './pages/PgpPage';
import OperationsPage from './pages/OperationsPage';
import { appConfig, APP_MODE_VALUE } from './config/appConfig';
import { ToastProvider } from './components/shared/Toast';

// Placeholder components with new styling
const Dashboard = () => (
  <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Dashboard</h1>
    <div className="space-y-4">
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Recent Activity</h2>
        <p className="text-gray-600">Your recent activity will appear here</p>
      </div>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Quick Stats</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">42</p>
            <p className="text-sm text-gray-600">Total Songs</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">1.2K</p>
            <p className="text-sm text-gray-600">Total Streams</p>
          </div>
          <div className="text-center sm:col-span-1 col-span-2">
            <p className="text-2xl font-bold text-red-600">89%</p>
            <p className="text-sm text-gray-600">Growth</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      // navigator.onLine is unreliable (can be stuck false even when the API is
      // reachable). Without this, React Query pauses queries and the table data
      // fetch never fires — spinner forever, no request, no error. 'always'
      // makes queries run regardless of the browser's online status.
      networkMode: 'always'
    },
    mutations: {
      networkMode: 'always'
    }
  }
});

function App() {
  // Select the appropriate streaming page based on mode. Encore roster now uses
  // the mobile-friendly v2 page; PGP keeps its dedicated page.
  const StreamingComponent = APP_MODE_VALUE === 'pgp' ? PGPStreamingPage : StreamingPageV2;
  const rootRequiredPerm = APP_MODE_VALUE === 'pgp' ? 'pgp' : 'streaming';

  // Set the document title and favicon based on app mode
  useEffect(() => {
    document.title = appConfig.pageTitle;

    // Update favicon dynamically
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon && appConfig.favicon) {
      favicon.href = `${process.env.PUBLIC_URL}/${appConfig.favicon}`;
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <Router>
            <div className="min-h-screen bg-gray-50">
              <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute requiredPerm={rootRequiredPerm}>
                    <>
                      <Header />
                      <main className="container mx-auto px-1 sm:px-6 lg:px-8 py-6 max-w-9xl">
                        <StreamingComponent />
                      </main>
                    </>
                  </ProtectedRoute>
                }
              />
              {/* Conditionally render Unsigned page only for Encore mode */}
              {appConfig.showUnsignedPage && (
                <Route
                  path="/unsigned"
                  element={
                    <ProtectedRoute requiredPerm="unsigned">
                      <>
                        <Header />
                        <main className="container mx-auto px-1 sm:px-6 lg:px-8 pt-2 pb-6 sm:py-6 max-w-9xl">
                          <UnsignedPageV2 />
                        </main>
                      </>
                    </ProtectedRoute>
                  }
                />
              )}
              {/* Legacy v2 URLs now redirect to the canonical routes. */}
              <Route path="/v2" element={<Navigate to="/" replace />} />
              <Route path="/v2unsigned" element={<Navigate to="/unsigned" replace />} />
              {/* Conditionally render Scheduler page only for Encore mode */}
              {appConfig.showScheduler && (
                <Route
                  path="/scheduler"
                  element={
                    <ProtectedRoute requiredPerm="scheduler">
                      <>
                        <Header />
                        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 max-w-9xl">
                          <SchedulerPage />
                        </main>
                      </>
                    </ProtectedRoute>
                  }
                />
              )}
              {/* Snoop also lives in the Unsigned page's source selector; these
                  standalone routes are kept for bookmarks and deep links. */}
              {appConfig.showSnoop && (
                <Route
                  path="/snoop"
                  element={
                    <ProtectedRoute requiredPerm="unsigned">
                      <>
                        <Header />
                        <main className="container mx-auto px-1 sm:px-6 lg:px-8 py-6 max-w-9xl">
                          <SnoopSummaryPage />
                        </main>
                      </>
                    </ProtectedRoute>
                  }
                />
              )}
              {/* Snoop mapping moved into the Mapping suite; keep the old
                  bookmark working. */}
              <Route path="/snoop/mapping" element={<Navigate to="/mapping/snoop" replace />} />
              <Route path="/mapping" element={<Navigate to="/mapping/snoop" replace />} />
              {/* Luminate's mapping views moved to the Mapping suite (they are
                  admin-only, unlike the Luminate discovery pages). */}
              <Route path="/luminate/mapping" element={<Navigate to="/mapping/luminate" replace />} />
              <Route path="/luminate/p-line" element={<Navigate to="/mapping/p-line" replace />} />
              {/* Luminate and Deals live in the Unsigned page's source
                  selector; these standalone routes are kept for bookmarks and
                  deep links (and reach Luminate Agg, which has no pill). */}
              {appConfig.showLuminate && (
                <Route
                  path="/luminate/*"
                  element={
                    <ProtectedRoute requiredPerm="unsigned">
                      <>
                        <Header />
                        <main className="container mx-auto px-1 sm:px-6 lg:px-8 py-6 max-w-9xl">
                          <LuminatePage />
                        </main>
                      </>
                    </ProtectedRoute>
                  }
                />
              )}
              {appConfig.showDeals && (
                <Route
                  path="/deals"
                  element={
                    <ProtectedRoute requiredPerm="unsigned">
                      <>
                        <Header />
                        <main className="container mx-auto px-1 sm:px-6 lg:px-8 py-6 max-w-9xl">
                          <DealsPage />
                        </main>
                      </>
                    </ProtectedRoute>
                  }
                />
              )}
              {/* Operations suite: Catalog / Commitments / Process / Updates.
                  Gated on the `operations` perm. Available on both builds. */}
              {appConfig.showOperations && (
                <Route path="/operations" element={<Navigate to="/operations/assets" replace />} />
              )}
              {appConfig.showOperations && (
                <Route
                  path="/operations/*"
                  element={
                    <ProtectedRoute requiredPerm="operations">
                      <>
                        <Header />
                        <main className="container mx-auto px-1 sm:px-6 lg:px-8 py-6 max-w-9xl">
                          <OperationsPage />
                        </main>
                      </>
                    </ProtectedRoute>
                  }
                />
              )}
              {/* PGP publishing suite (PGP build only): Agenda · Charts ·
                  Roster on the `pgp` perm, Mapping · Genius on `pgp_admin`.
                  The admin routes come first so they win over /pgp/*. */}
              {appConfig.showPgpSuite && (
                <Route path="/pgp" element={<Navigate to="/pgp/agenda" replace />} />
              )}
              {appConfig.showPgpSuite &&
                ['/pgp/mapping', '/pgp/genius'].map((path) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <ProtectedRoute requiredPerm="pgp_admin">
                        <>
                          <Header />
                          <main className="container mx-auto px-1 sm:px-6 lg:px-8 py-6 max-w-9xl">
                            <PgpPage />
                          </main>
                        </>
                      </ProtectedRoute>
                    }
                  />
                ))}
              {appConfig.showPgpSuite && (
                <Route
                  path="/pgp/*"
                  element={
                    <ProtectedRoute requiredPerm="pgp">
                      <>
                        <Header />
                        <main className="container mx-auto px-1 sm:px-6 lg:px-8 py-6 max-w-9xl">
                          <PgpPage />
                        </main>
                      </>
                    </ProtectedRoute>
                  }
                />
              )}
              {/* Mapping suite: the tables that drive the discovery feeds.
                  Admin-only, and present if either feed is enabled. */}
              {(appConfig.showSnoop || appConfig.showLuminate) && (
                <Route
                  path="/mapping/*"
                  element={
                    <ProtectedRoute requireAdmin>
                      <>
                        <Header />
                        <main className="container mx-auto px-1 sm:px-6 lg:px-8 py-6 max-w-9xl">
                          <MappingPage />
                        </main>
                      </>
                    </ProtectedRoute>
                  }
                />
              )}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAdmin>
                    <>
                      <Header />
                      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 max-w-9xl">
                        <AdminPage />
                      </main>
                    </>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <>
                      <Header />
                      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 max-w-9xl">
                        <Dashboard />
                      </main>
                    </>
                  </ProtectedRoute>
                }
              />
              </Routes>
            </div>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App; 